// writeflow-tutor/frontend/src/App.js

import React from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

// Public Pages
import LandingPage from "./pages/LandingPage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import PasswordRecoveryPage from "./pages/PasswordRecoveryPage.jsx";
import EnrollmentPage from "./pages/EnrollmentPage.jsx";
import CertificateVerifyPage from "./pages/CertificateVerifyPage.jsx";

// Student Components
import StudentDashboard from "./components/StudentDashboard";
import LessonPage from "./components/LessonPage";
import WeekOneEntryGate from "./components/demo/WeekOneEntryGate";
import GuidedDemoPage from "./components/demo/GuidedDemoPage";
import StudentPortalHome from "./components/StudentPortalHome";
import StudentAccount from "./components/StudentAccount";
import StudentBadges from "./components/StudentBadges";
import StudentLessonsTab from "./components/StudentLessonsTab";
import StudentNotifications from "./components/StudentNotifications";
import StudentTutorNotes from "./components/StudentTutorNotes";
import ReflectionPage from "./components/ReflectionPage";
import CourseCompletePage from "./components/CourseCompletePage";
import MentorPage from "./components/MentorPage";
import MentorPreparationPage from "./components/MentorPreparationPage";
import WeekStartPage from "./components/WeekStartPage";

// Tutor Components
import TutorStudentList from "./components/TutorStudentList";
import TutorCurriculumExplorer from "./components/TutorCurriculumExplorer";
import TutorWorkspaceShell from "./components/TutorWorkspaceShell";
import AdminEnrollmentsPage from "./pages/admin/AdminEnrollmentsPage.jsx";
import AdminEnrollmentDetailPage from "./pages/admin/AdminEnrollmentDetailPage.jsx";
import AdminUsersPage from "./pages/admin/AdminUsersPage.jsx";
import AdminUserDetailPage from "./pages/admin/AdminUserDetailPage.jsx";
import AdminHomePage from "./pages/admin/AdminHomePage.jsx";
import ComingSoonAdminPage from "./pages/admin/ComingSoonAdminPage.jsx";

// Tutor workflow pages
import TutorNeedsReviewPage from "./tutor_v2/TutorNeedsReviewPage";
import TutorStudentProgressPage from "./tutor_v2/TutorStudentProgressPage";
import TutorReviewWorkspace from "./tutor_v2/TutorReviewWorkspace";
import TutorLandingRedirect from "./tutor_v2/TutorLandingRedirect";

// New Student Dashboard V2
import DashboardV2 from "./dashboard_v2/DashboardV2";

// Redirect helper for /student/:studentId
import StudentRootRedirect from "./components/StudentRootRedirect";
import { resolveUserRole } from "./utils/userRoles";
import StudentViewBanner from "./components/StudentViewBanner";
import { API_BASE_URL } from "./config/apiBase";
import { hasStaleEmailAuthState, persistCanonicalAuthUser, readStoredAuthUser } from "./utils/userRoles";
import { buildStudentRoute, normalizeStudentId } from "./utils/studentRoutes";

function AuthStateBootstrap() {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    const stored = readStoredAuthUser();
    const storedId = normalizeStudentId(
      localStorage.getItem("student_id") || sessionStorage.getItem("student_id") || stored?.student_id || stored?.username
    );
    const malformedStudentPath = /^\/student\/(undefined|null)(?:\/|$)/i.test(window.location.pathname);
    if (!hasStaleEmailAuthState()) {
      if (malformedStudentPath && storedId) {
        const repairedPath = window.location.pathname.replace(
          /^(\/student\/)[^/]+/i,
          `${buildStudentRoute(storedId, "").slice(0, -1)}`,
        );
        window.history.replaceState({}, "", repairedPath + window.location.search + window.location.hash);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/auth/me`, { headers: { "X-User": storedId }, cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled) return;
        if (payload?.user && persistCanonicalAuthUser(payload.user)) {
          const nextId = localStorage.getItem("student_id");
          if (nextId) {
            const repairedPath = window.location.pathname.replace(
              /^(\/student\/)[^/]+/i,
              `$1${encodeURIComponent(nextId)}`,
            );
            window.history.replaceState({}, "", repairedPath + window.location.search + window.location.hash);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        }
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        ["student_id", "student_name", "role", "is_admin", "payment_made", "auth_provider", "google_email", "auth_user"].forEach((key) => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });
        setReady(true);
      });
    return () => { cancelled = true; };
  }, []);
  return ready ? null : <div aria-live="polite">Loading your account…</div>;
}

function LegacyDashboardLessonRedirect() {
  const { id, weekId, lessonId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/student/dashboard/${id}/lessons/week/${weekId}/lesson/${lessonId}${location.search || ""}${location.hash || ""}`}
      replace
    />
  );
}

function TutorRoute({ element }) {
  const studentId = String(localStorage.getItem("student_id") || "").toUpperCase();
  const role = resolveUserRole(studentId, localStorage.getItem("role"));
  if (role !== "tutor" && role !== "admin") {
    return <Navigate to={studentId ? `/student/dashboard/${studentId}` : "/login"} replace />;
  }
  return element;
}

function AdminRoute({ element }) {
  const studentId = String(localStorage.getItem("student_id") || "").toUpperCase();
  const role = resolveUserRole(studentId, localStorage.getItem("role"));
  if (role !== "admin") {
    return <Navigate to={studentId ? `/student/dashboard/${studentId}` : "/login"} replace />;
  }
  return element;
}

function TutorCurriculumRoute() {
  const studentId = String(localStorage.getItem("student_id") || "").toUpperCase();
  return <TutorCurriculumExplorer studentId={studentId} />;
}

function TutorAccountRoute() {
  const studentId = String(localStorage.getItem("student_id") || "").toUpperCase();
  const studentName = localStorage.getItem("student_name") || "";
  return <StudentAccount outletContext={{ studentId, studentName, tutorMode: true }} />;
}

function TutorNotificationsRoute() {
  const studentId = String(localStorage.getItem("student_id") || "").toUpperCase();
  const studentName = localStorage.getItem("student_name") || "";
  return (
    <StudentNotifications
      outletContext={{
        studentId,
        studentName,
        tutorMode: true,
        goToTutorNotes: () => {},
      }}
    />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthStateBootstrap />
      <StudentViewBanner />
      <Routes>
        {/* =====================
            PUBLIC ROUTES
        ====================== */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
        <Route path="/reset-password" element={<PasswordRecoveryPage reset />} />
        <Route path="/enroll" element={<EnrollmentPage />} />
        <Route path="/verify/:certificateId" element={<CertificateVerifyPage />} />

        {/* =====================
            TUTOR ROUTES
        ====================== */}
        <Route path="/tutor" element={<TutorRoute element={<TutorWorkspaceShell />} />}>
          <Route index element={<TutorLandingRedirect />} />
          <Route path="needs-review" element={<TutorNeedsReviewPage />} />
          <Route path="student-progress" element={<TutorStudentProgressPage />} />
          <Route path="dashboard_v2" element={<Navigate to="/tutor/needs-review" replace />} />
          <Route path="dashboard" element={<Navigate to="/tutor/needs-review" replace />} />
          <Route path="curriculum" element={<TutorCurriculumRoute />} />
          <Route path="curriculum/week/:weekId/lesson/:lessonId" element={<TutorCurriculumRoute />} />
          <Route path="student/:studentId" element={<TutorStudentList />} />
          <Route path="student/:studentId/week/:weekId/lesson/:lessonId" element={<TutorStudentList />} />
          <Route path="review/:studentId/week/:weekId/lesson/:lessonId" element={<TutorReviewWorkspace />} />
          <Route path="account" element={<TutorAccountRoute />} />
          <Route path="notifications" element={<TutorNotificationsRoute />} />
        </Route>
        <Route path="/tutor/review-queue" element={<Navigate to="/tutor/needs-review" replace />} />
        <Route path="/admin" element={<AdminRoute element={<AdminHomePage />} />} />
        <Route path="/admin/enrollments" element={<AdminRoute element={<AdminEnrollmentsPage />} />} />
        <Route path="/admin/enrollments/:id" element={<AdminRoute element={<AdminEnrollmentDetailPage />} />} />
        <Route path="/admin/users" element={<AdminRoute element={<AdminUsersPage />} />} />
        <Route path="/admin/users/:userId" element={<AdminRoute element={<AdminUserDetailPage />} />} />
        <Route
          path="/admin/cohorts"
          element={<AdminRoute element={<ComingSoonAdminPage title="Cohorts" subtitle="Cohort management will be added here." />} />}
        />
        <Route
          path="/admin/audit-log"
          element={<AdminRoute element={<ComingSoonAdminPage title="Audit Log" subtitle="Audit events will be shown here." />} />}
        />

        {/* =====================
            STUDENT DASHBOARD V2
        ====================== */}
        <Route path="/student/:studentId/dashboard_v2" element={<DashboardV2 />} />

        <Route path="/student/:studentId" element={<StudentDashboard />}>
          <Route index element={<StudentRootRedirect />} />
          <Route
            path="week/:weekId/lesson/:lessonId"
            element={
              <WeekOneEntryGate>
                <LessonPage />
              </WeekOneEntryGate>
            }
          />
          <Route path="week-reflection/:weekId" element={<ReflectionPage />} />
          <Route path="week-mentor/:weekId" element={<MentorPage />} />
          <Route path="week-mentor-preparing/:weekId" element={<MentorPreparationPage />} />
          <Route path="week/:weekId/start" element={<WeekStartPage />} />
          <Route path="course-complete" element={<CourseCompletePage />} />
        </Route>
        <Route path="/guided-demo" element={<GuidedDemoPage />} />

        {/* =====================
            STUDENT LESSON ROUTES
        ====================== */}
        {/* Legacy route (no week in URL) – keep for old links if any */}
        <Route path="/student/:id/lesson/:lessonId" element={<LessonPage />} />

        {/* =====================
            STUDENT PORTAL (Legacy URL, now a layout)
        ====================== */}
        <Route path="/student/dashboard/:id" element={<StudentDashboard />}>
          <Route index element={<StudentPortalHome />} />
          <Route path="guided-demo" element={<GuidedDemoPage />} />
          <Route path="dashboard_v2" element={<DashboardV2 />} />
          <Route path="lessons" element={<StudentLessonsTab />}>
            <Route index element={<Navigate to="week/1/lesson/1" replace />} />
            <Route
              path="week/:weekId/lesson/:lessonId"
              element={
                <WeekOneEntryGate>
                  <LessonPage />
                </WeekOneEntryGate>
              }
            />
            <Route path="week-reflection/:weekId" element={<ReflectionPage />} />
            <Route path="week-mentor/:weekId" element={<MentorPage />} />
            <Route path="week-mentor-preparing/:weekId" element={<MentorPreparationPage />} />
            <Route path="week/:weekId/start" element={<WeekStartPage />} />
            <Route path="course-complete" element={<CourseCompletePage />} />
          </Route>
          <Route path="notifications" element={<StudentNotifications />} />
          <Route path="tutor-notes" element={<StudentTutorNotes />} />
          <Route path="account" element={<StudentAccount />} />
          <Route path="badges" element={<StudentBadges />} />
          <Route path="week/:weekId/lesson/:lessonId" element={<LegacyDashboardLessonRedirect />} />
          <Route
            path="week-reflection/:weekId"
            element={<ReflectionPage />}
          />
          <Route path="week-mentor/:weekId" element={<MentorPage />} />
          <Route path="week-mentor-preparing/:weekId" element={<MentorPreparationPage />} />
          <Route path="week/:weekId/start" element={<WeekStartPage />} />
          <Route path="course-complete" element={<CourseCompletePage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
